import { assistantRunPlanSchema } from '@crowdex/agent-contracts/api';
import { z } from 'zod';
import {
  pageHeaderSchema,
  pageInspectorSchema,
  pageNavigateSchema,
  pageSectionsSchema,
} from './pages';

/** Headers the environment sets on every request it hands to an app (section 5 of the plan). */
export const APP_CONTEXT_HEADER = 'x-crowdex-context';
export const APP_TOKEN_HEADER = 'x-crowdex-token';

/** Where a view is shown; views adapt to their container. */
export const VIEW_CONTAINERS = ['main', 'side', 'dialog', 'window', 'preview', 'call'] as const;
export const viewContainerSchema = z.enum(VIEW_CONTAINERS);
export type ViewContainer = z.infer<typeof viewContainerSchema>;

/**
 * `X-Crowdex-Context`: who asks and for what. `user` is an opaque reference distinct per
 * plugin, never the account id. `api` is the environment's origin serving the capability API.
 */
export const appRequestContextSchema = z
  .object({
    plugin: z.string(),
    version: z.string(),
    environment: z.enum(['local', 'cloud']),
    api: z.string(),
    user: z.string(),
    /** The request runs in a workspace: its `workspace:` files, commands and browser. */
    workspace: z.boolean().optional(),
    /** The conversation the request comes from, when the plugin started it (`agents.run`). */
    conversation: z.string().optional(),
    item: z.string().optional(),
    artifact: z.object({ space: z.string(), path: z.string() }).optional(),
    container: viewContainerSchema.optional(),
    /** The plugin's settings as they apply here: defaults, the user's, the workspace's. */
    settings: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
  })
  .strict();
export type AppRequestContext = z.infer<typeof appRequestContextSchema>;

/** The capability API path an app calls with its token: `/api/capabilities/v1/<capability>/<method>`. */
export function capabilityPath(capability: string, method: string): string {
  return `/api/capabilities/v1/${encodeURIComponent(capability)}/${encodeURIComponent(method)}`;
}

/** An error the capability API answers with (`{ error: { code, message } }`). */
export const capabilityErrorSchema = z.object({
  error: z.object({
    code: z.enum([
      'unauthorized',
      'forbidden',
      'invalid_request',
      'not_found',
      'conflict',
      'unsupported',
      /** What the capability ran failed (a connector action an API refused). */
      'failed',
    ]),
    message: z.string(),
  }),
});

// --- storage ---------------------------------------------------------------------------------

/**
 * `account`: the user's documents on the server that holds their account, shared by their servers:
 * a Desktop linked to Crowdex Cloud keeps them on the cloud; elsewhere they are the `user` scope's.
 */
export const STORAGE_SCOPES = ['workspace', 'user', 'org', 'account'] as const;
export const storageScopeSchema = z.enum(STORAGE_SCOPES);
export type StorageScope = z.infer<typeof storageScopeSchema>;

const collectionSchema = z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9._-]{0,63}$/);
const documentIdSchema = z.string().min(1).max(256);
const indexValueSchema = z.union([z.string().max(512), z.number().finite(), z.boolean()]);

/** Up to eight indexed fields per document, queryable by equality and range. */
export const storageIndexesSchema = z
  .record(z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,31}$/), indexValueSchema)
  .refine((indexes) => Object.keys(indexes).length <= 8, 'At most 8 indexed fields');

const storageTarget = {
  scope: storageScopeSchema,
  collection: collectionSchema,
  id: documentIdSchema,
};

export const storageGetInputSchema = z.object(storageTarget).strict();
export const storagePutInputSchema = z
  .object({
    ...storageTarget,
    value: z.unknown(),
    indexes: storageIndexesSchema.optional(),
    /** Write only if the stored version is this one (`null`: only if absent). */
    version: z.number().int().nonnegative().nullable().optional(),
  })
  .strict();
export const storageDeleteInputSchema = z
  .object({ ...storageTarget, version: z.number().int().nonnegative().optional() })
  .strict();
const rangeSchema = z
  .object({
    gt: indexValueSchema.optional(),
    gte: indexValueSchema.optional(),
    lt: indexValueSchema.optional(),
    lte: indexValueSchema.optional(),
  })
  .strict();
export const storageQueryInputSchema = z
  .object({
    scope: storageScopeSchema,
    collection: collectionSchema,
    where: z.record(z.string(), z.union([indexValueSchema, rangeSchema])).optional(),
    order: z
      .object({ field: z.string(), direction: z.enum(['asc', 'desc']).default('asc') })
      .strict()
      .optional(),
    limit: z.number().int().min(1).max(500).default(100),
    cursor: z.string().max(512).optional(),
  })
  .strict();
export type StorageDocument = {
  id: string;
  value: unknown;
  indexes: Record<string, string | number | boolean>;
  version: number;
  updatedAt: string;
};
/** Collections the core keeps for a plugin (its settings); apps read them, never write. */
export const CORE_STORAGE_COLLECTION_PREFIX = 'crowdex.';
export const SETTINGS_COLLECTION = 'crowdex.settings';

/** Documents are at most 1 MiB of JSON. */
export const MAX_STORAGE_DOCUMENT_BYTES = 1024 * 1024;

// --- events ----------------------------------------------------------------------------------

/**
 * An event of one of the types the plugin declares (`events` of its manifest): stored for the
 * user, told to them as their settings of its type say, and handed to the agents subscribed.
 */
export const eventPublishInputSchema = z
  .object({
    /** One of the plugin's declared types, without its `<plugin>/` prefix. */
    type: z.string().min(1).max(64),
    title: z.string().min(1).max(200),
    body: z.string().max(2000).optional(),
    /** The type's severity by default. */
    severity: z.enum(['info', 'success', 'warning', 'error']).optional(),
    /** The type's declared fields, checked against their declared kinds. */
    fields: z
      .record(z.string(), z.union([z.string().max(512), z.number(), z.boolean()]))
      .optional(),
    /** A route of the plugin's page to open. */
    link: z.string().max(512).optional(),
    /** An external page to open instead (`https://`). */
    url: z
      .string()
      .max(2048)
      .refine((value) => value.startsWith('https://'), 'URLs are https://.')
      .optional(),
    /** Events of a group update one another: a new one replaces the group's unread ones. */
    group: z.string().min(1).max(200).optional(),
    /** The conversation it is about (the user's), opened from it. */
    conversation: z.uuid().optional(),
  })
  .strict();
export type EventPublishInput = z.infer<typeof eventPublishInputSchema>;

const eventFilterValueSchema = z.union([z.string().max(512), z.number(), z.boolean()]);

/**
 * `events.subscribe`: the app hears each matching event on `POST /triggers/<id>` (`kind:
 * 'event'`). It may subscribe to its own types (`<plugin>/…`), and to the core's agent events
 * (`crowdex/agent.*`) of a conversation it started (`filter.conversation`).
 */
export const eventSubscribeInputSchema = z
  .object({
    /** The app's id for it; subscribing again with it replaces it. */
    id: z.string().regex(/^[a-zA-Z0-9:._-]{1,128}$/),
    types: z
      .array(z.string().regex(/^[a-z0-9][a-z0-9._-]*\/(?:[a-zA-Z0-9._-]+|[a-zA-Z0-9._-]*\*)$/))
      .min(1)
      .max(16),
    filter: z
      .record(
        z.string().max(64),
        z.union([eventFilterValueSchema, z.array(eventFilterValueSchema).max(32)]),
      )
      .default({}),
    once: z.boolean().default(false),
    expiresInMinutes: z
      .number()
      .int()
      .positive()
      .max(30 * 24 * 60)
      .optional(),
  })
  .strict();
export type EventSubscribeInput = z.input<typeof eventSubscribeInputSchema>;

export const eventUnsubscribeInputSchema = z.object({ id: z.string().max(128) }).strict();

// --- metrics ---------------------------------------------------------------------------------

/**
 * What the core counts of a user's Crowdex, read-only (`metrics.query`): each metric with the
 * dimensions it groups and filters by. `turns` are the agents' replies, `cost` what the agents
 * reported spending; `events` only reach back as far as the events the core keeps.
 */
export const METRICS = {
  conversations: {
    title: 'Conversations started',
    unit: 'count',
    dimensions: ['workspace', 'agent', 'model', 'status', 'tag', 'collaboration'],
  },
  turns: {
    title: 'Agent replies',
    unit: 'count',
    dimensions: ['workspace', 'agent', 'model', 'tag', 'outcome'],
  },
  cost: {
    title: 'Agent spend',
    unit: 'currency',
    dimensions: ['workspace', 'agent', 'model', 'tag'],
  },
  artifacts: {
    title: 'Artifacts changed',
    unit: 'count',
    dimensions: ['plugin', 'type', 'space'],
  },
  events: {
    title: 'Recent events',
    unit: 'count',
    dimensions: ['type', 'plugin', 'severity'],
  },
  connections: {
    title: 'Connections',
    unit: 'count',
    dimensions: ['service', 'status'],
  },
} as const satisfies Record<
  string,
  { title: string; unit: 'count' | 'currency'; dimensions: readonly string[] }
>;
export type MetricName = keyof typeof METRICS;
export const METRIC_NAMES = Object.keys(METRICS) as MetricName[];

const metricFilterValueSchema = z.string().min(1).max(256);

export const metricQueryInputSchema = z
  .object({
    metric: z.enum(METRIC_NAMES as [MetricName, ...MetricName[]]),
    /** The time range, on the metric's time (a conversation's start, a reply's end…). */
    from: z.iso.datetime({ offset: true }).optional(),
    to: z.iso.datetime({ offset: true }).optional(),
    /** A series: one row per period (UTC), else one total. */
    interval: z.enum(['day', 'week', 'month']).optional(),
    /** One of the metric's dimensions: one row per value (per period). */
    groupBy: z.string().max(32).optional(),
    /** Values a dimension must have (one of a list). */
    filter: z
      .record(
        z.string().max(32),
        z.union([metricFilterValueSchema, z.array(metricFilterValueSchema).min(1).max(32)]),
      )
      .default({}),
    /** The groups kept, largest first (the others are left out). */
    limit: z.number().int().positive().max(100).default(20),
  })
  .strict()
  .superRefine((input, context) => {
    const dimensions: readonly string[] = METRICS[input.metric].dimensions;
    for (const name of [input.groupBy, ...Object.keys(input.filter)])
      if (name !== undefined && !dimensions.includes(name))
        context.addIssue({
          code: 'custom',
          message: `${input.metric} has no dimension "${name}" (${dimensions.join(', ')}).`,
        });
  });
export type MetricQueryInput = z.input<typeof metricQueryInputSchema>;

export type MetricRow = {
  /** The period's start, with `interval`. */
  at?: string;
  /** The dimension's value, with `groupBy`. */
  group?: string;
  value: number;
};
export type MetricResult = {
  metric: MetricName;
  unit: 'count' | 'currency';
  /** With `cost`: the currency of the values (the user's main one; others are left out). */
  currency?: string;
  rows: MetricRow[];
};

// --- views -----------------------------------------------------------------------------------

/** The bridge operations a view may call (`window.crowdex`). */
export const BRIDGE_OPERATIONS = [
  'context.get',
  'context.subscribe',
  'agent.ask',
  'agent.attach',
  'view.open',
  'dialog.close',
  'host.confirm',
  'page.select',
  'page.refresh',
  'page.sections.set',
  'page.header.set',
  'page.navigate',
  'page.back',
  'page.inspector.open',
  'page.inspector.close',
] as const;
export type BridgeOperation = (typeof BRIDGE_OPERATIONS)[number];

/**
 * Where the host shows a view, so it can lay itself out: full screen (an app's page, an item),
 * docked as a tab of a conversation's side panel, in a dialog, embedded in a card, or as the
 * inspector of another of its views (`page.inspector.open`, the 320 px column of the frame).
 */
export const VIEW_LAYOUTS = ['full', 'docked', 'dialog', 'embedded', 'inspector'] as const;
export type ViewLayout = (typeof VIEW_LAYOUTS)[number];

/** Who made what a view shows: an assistant, and its conversation when known. */
export type ViewOrigin = {
  assistant: { id: string; name: string; color?: string | null };
  conversation?: string;
};

/** What a view reads from `context.get`. */
export type ViewContext = {
  plugin: string;
  container: ViewContainer;
  /** Where the host shows it (`full`, `docked`, `dialog`, `embedded`); absent from older hosts. */
  layout?: ViewLayout;
  /** The view's route (`/runs/42`, `page.navigate`), `/` on its first level. */
  route?: string;
  /**
   * Who made the item it shows, when the host knows (drawn by the host as "From <assistant>"); for
   * an avatar of a call plugin, the assistant it serves.
   */
  origin?: ViewOrigin | null;
  /**
   * The route open in the view's inspector (`page.inspector.open`), null while it is closed;
   * absent where the host gives the view no inspector (a dialog, a card, an older host).
   */
  inspector?: string | null;
  /**
   * The last action of the host's bar the user clicked (`page.header.set`): its id, or the id of
   * the choice of its menu, with a number growing at each click (the same action twice is two).
   */
  action?: { id: string; sequence: number } | null;
  /**
   * The host's theme: its mode, its tokens, and the design system's theme name (`data-theme`,
   * `desktop-dark`), which the bridge sets on the view so its own copy of the design system
   * resolves the same palette.
   */
  theme: { mode: 'light' | 'dark'; variables: Record<string, string>; name?: string | null };
  /** The view works in a workspace (the open conversation's): its files, commands, browser. */
  workspace: boolean;
  /** The item picked in the sidebar for the view (`page.sidebars`), and the section it is of. */
  item?: string | null;
  section?: string | null;
  /**
   * The item the user picked in one of the view's live sections (`page.sections.set`), until it
   * picks another: the view shows it (a design's page, a slide).
   */
  selection?: { section: string; item: string } | null;
  artifact?: { space: string; path: string; version?: string; readOnly: boolean };
  /** What opened the view handed it (`view.open({ input })`, an action's `input`). */
  input?: unknown;
  /**
   * A call view: where the core's voice call stands, how loud the voice it hears is now (0 to 1,
   * in steps of 0.05), and the voice's latest words, in order, as its transcript streams them
   * (about when they are heard; each id larger than the last), with the language of the call, for
   * a view that moves with it; and who speaks, which may change during the call.
   */
  call?: {
    state: 'connecting' | 'listening' | 'speaking' | 'working';
    muted: boolean;
    level?: number;
    spoken?: readonly { id: number; text: string }[];
    language?: string;
    /** The persona the call speaks as (`callPersonaSchema`'s id), which the view shows. */
    persona?: string;
  };
};

/**
 * A persona a call plugin's `call.personas` route lists: who the user's assistant is, in calls and
 * in its turns. Each persona is an assistant of its own: the core keeps one conversation per `id`
 * (stable while the persona lasts) and adds the persona to what the call's voice and that
 * assistant are told; the voice, an id of the user's voice-call provider (`voice.voices`),
 * replaces the one of Settings. `title` is how the user sees it in lists and names it in a call
 * ("put Marie on"); `name`, when the user gave one, is the name it introduces itself by.
 */
export const callPersonaSchema = z
  .object({
    id: z.string().trim().min(1).max(128),
    title: z.string().trim().min(1).max(80),
    name: z.string().trim().min(1).max(80).optional(),
    personality: z.string().max(4000).optional(),
    instructions: z.string().max(8000).optional(),
    voice: z.string().min(1).max(128).optional(),
  })
  .strip();
export type CallPersona = z.infer<typeof callPersonaSchema>;

/**
 * What a call plugin's `call.personas` route answers to `GET`, and to `POST { select }` once it
 * selected that persona: the personas the user can talk to, and the one selected (who answers a
 * new call and the assistant's turns outside calls).
 */
export const callPersonasSchema = z
  .object({
    selected: z.string().trim().min(1).max(128).nullable(),
    personas: z.array(callPersonaSchema).max(64),
  })
  .strip();
export type CallPersonas = z.infer<typeof callPersonasSchema>;

const rectSchema = z
  .object({ x: z.number(), y: z.number(), width: z.number(), height: z.number() })
  .strict();

/**
 * An element the user points at and comments on, for the agent: a web page's in the browser, a
 * design's on the Design page. The core shows it as a chip of the composer and sends it as an
 * `<element-selection>` block (context, never instructions), with its screenshot as a file.
 */
export const elementSelectionSchema = z
  .object({
    /** Where it is, for the user: a site, "Design · Landing page". */
    source: z.string().min(1).max(200),
    /** Where it is, for the agent: its page's URL, or a workspace file and line. */
    location: z.string().max(1000).optional(),
    /** What the element is: its label or tag. */
    title: z.string().max(300),
    selector: z.string().max(2000),
    /** Its start tag, or its markup cut short. */
    html: z.string().max(4000),
    text: z.string().max(4000),
    /** What the user says about it. */
    annotation: z.string().max(4000).optional(),
    /** The slide or screen it is on. */
    screen: z.string().max(200).optional(),
    /** A reference of the app's, which its tools understand (a comment's id). */
    reference: z.string().max(300).optional(),
    rect: rectSchema.optional(),
    viewport: z.object({ width: z.number(), height: z.number() }).strict().optional(),
    /** A PNG of it, as a data URL. */
    screenshot: z.string().max(1_500_000).startsWith('data:image/png;base64,').optional(),
  })
  .strict();
export type ElementSelection = z.infer<typeof elementSelectionSchema>;

export const bridgeInputSchemas = {
  'context.get': z.undefined().or(z.object({}).strict()),
  /** Asks the agent: the text goes to the open conversation, or a new one. */
  'agent.ask': z.object({ text: z.string().min(1).max(64_000) }).strict(),
  /**
   * Puts elements the user commented on in the composer of the open conversation, as the
   * browser's selections are; `send` sends them at once.
   */
  'agent.attach': z
    .object({
      selections: z.array(elementSelectionSchema).min(1).max(20),
      send: z.boolean().optional(),
    })
    .strict(),
  'view.open': z.union([
    z
      .object({
        path: z.string().startsWith('/').max(512),
        in: viewContainerSchema.exclude(['preview', 'call']).optional(),
        input: z.unknown().optional(),
        /** A dialog's width. */
        size: z.enum(['default', 'wide']).optional(),
      })
      .strict(),
    z
      .object({
        artifact: z.object({ space: z.string(), path: z.string() }).strict(),
        in: viewContainerSchema.exclude(['preview', 'call']).optional(),
      })
      .strict(),
    /** A conversation of the user (an automation run's), shown by the core. */
    z.object({ conversation: z.uuid() }).strict(),
    /** Where the user connects an account of a service (`github`), to reconnect it. */
    z
      .object({
        connect: z.string().min(1).max(256),
        /** The connection to sign in again, when the view knows which one failed. */
        connection: z.string().min(1).max(256).optional(),
      })
      .strict(),
  ]),
  'dialog.close': z.object({ result: z.unknown().optional() }).strict().optional(),
  'host.confirm': z
    .object({
      title: z.string().min(1).max(200),
      message: z.string().max(2000).optional(),
      confirm: z.string().max(40).optional(),
      destructive: z.boolean().optional(),
    })
    .strict(),
  'page.select': z
    .object({
      item: z.string().max(256).nullable(),
      /** The sidebar (`page.sidebars` id) the item is of; the first one when left out. */
      section: z.string().max(64).optional(),
    })
    .strict(),
  'page.refresh': z.undefined().or(z.object({}).strict()),
  'page.sections.set': pageSectionsSchema,
  /** The host's bar over the view: its title, its actions (one primary at most), its status. */
  'page.header.set': pageHeaderSchema,
  /** Goes to a route of the view: the host keeps the address, the column and the chevron. */
  'page.navigate': pageNavigateSchema,
  /** Goes up a level, as the chevron of the host's bar and ⌘[ do (after a deletion). */
  'page.back': z.undefined().or(z.object({}).strict()),
  /** Opens a route of the view in the inspector, the 320 px column at the right of the frame. */
  'page.inspector.open': pageInspectorSchema,
  /** Closes the inspector. */
  'page.inspector.close': z.undefined().or(z.object({}).strict()),
} as const;

// --- triggers --------------------------------------------------------------------------------

const triggerIdSchema = z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9._:-]{0,127}$/);
/**
 * How a timer repeats: by a cron expression in a timezone, or every so many seconds. The core
 * keeps the clock (docs/adr-tasks-and-roles.md §1): it fires each occurrence and arms the next.
 */
export const timerRepeatSchema = z.union([
  z
    .object({ cron: z.string().trim().min(1).max(120), timezone: z.string().min(1).max(80) })
    .strict(),
  z.object({ everySeconds: z.number().int().min(5).max(31_536_000) }).strict(),
]);
export type TimerRepeat = z.infer<typeof timerRepeatSchema>;

export const timerSetInputSchema = z
  .object({
    id: triggerIdSchema,
    /** A one-shot timer's time. */
    at: z.iso.datetime({ offset: true }).optional(),
    /** A repeating timer: its first occurrence is the next one from now. */
    repeat: timerRepeatSchema.optional(),
    /**
     * What a repeating timer does with occurrences missed while the server was down: `latest`
     * fires once for all of them, `none` skips them.
     */
    catchUp: z.enum(['latest', 'none']).default('latest'),
    /** Fire in the workspace of the request that set it (its files, commands, browser). */
    inWorkspace: z.boolean().default(false),
  })
  .strict()
  .refine((input) => (input.at === undefined) !== (input.repeat === undefined), {
    message: 'Give `at` or `repeat`.',
  });
export const triggerIdInputSchema = z.object({ id: triggerIdSchema }).strict();
export const urlCreateInputSchema = z
  .object({ id: triggerIdSchema, inWorkspace: z.boolean().default(false) })
  .strict();
/** At most this many timers and URLs per plugin and user. */
export const MAX_TRIGGERS = 1000;

/** What `POST /triggers/:id` receives: a timer firing or a request to a webhook URL. */
export type TriggerEvent =
  /** A timer's occurrence: `at` is when it was due (a repeating timer's is armed again). */
  | { kind: 'timer'; id: string; at: string }
  | {
      kind: 'url';
      id: string;
      method: string;
      headers: Record<string, string>;
      query: Record<string, string>;
      body: string;
    }
  /** An event of one of the app's subscriptions (`events.subscribe`), whose id it carries. */
  | { kind: 'event'; id: string; event: TriggeredEvent }
  /** The user's answer to one of its requests (`requests.create`), whose id it carries. */
  | { kind: 'request'; id: string; answer: RequestAnswer };

/** An event as a subscribed app receives it. */
export type TriggeredEvent = {
  id: string;
  /** `<publisher>/<type>`. */
  type: string;
  title: string;
  body: string | null;
  severity: 'info' | 'success' | 'warning' | 'error';
  fields: Record<string, string | number | boolean>;
  /** The conversation it is about. */
  conversation: string | null;
  createdAt: string;
};

// --- tasks: the `tasks.v1` provider's routes ---------------------------------------------------

/**
 * What the core sends a provider's `tasks.start` route (docs/adr-tasks-and-automations.md): run
 * one of the user's automations (`automation`, an id its `list` answered) for an assistant's
 * task; a core of before names a workflow of the user's registry (`workflow`, with its
 * `definition`) instead. The core made the task's conversation first (a child of the conversation
 * that asked, tagged with the plugin's name): the run works in it and reports with
 * `tasks.report`. Answered `taskStartedSchema`.
 */
export const taskStartSchema = z
  .object({
    task: z
      .object({
        /** The task's conversation: the run's own. */
        conversation: z.uuid(),
        text: z.string().min(1).max(100_000),
        requester: z.string().min(1).max(200),
      })
      .strict(),
    /** The automation it runs: an id of the provider's `list`. */
    automation: z.string().min(1).max(128).optional(),
    /** @deprecated A workflow of the user's registry, from a core of before `automation`. */
    workflow: z
      .object({
        id: z.string().min(1).max(128),
        name: z.string().min(1).max(80),
        /** The provider's definition it runs (`template:standard`, a file of the workspace). */
        definition: z.string().min(1).max(512),
      })
      .strict()
      .optional(),
    project: z
      .object({
        id: z.string().min(1).max(128),
        name: z.string().min(1).max(80),
        /** The conversation that asked for the task, its parent. */
        conversation: z.uuid(),
        /**
         * Its workspace (Crowdex's, not a checkout): a template runs as that workspace's copy.
         * Left out once workspaces go (docs/adr-assistants.md): the copy is the user's.
         */
        workspace: z.string().min(1).max(128).optional(),
      })
      .strict(),
    /**
     * The assistant the task is for (docs/adr-assistants.md), sent beside `project` until
     * `project` goes: the same id, name and asking conversation.
     */
    assistant: z
      .object({
        id: z.string().min(1).max(128),
        name: z.string().min(1).max(80),
        conversation: z.uuid(),
      })
      .strict()
      .optional(),
  })
  .strict()
  .refine((input) => input.automation !== undefined || input.workflow !== undefined, {
    message: 'Name the automation it runs.',
  });
export type TaskStart = z.infer<typeof taskStartSchema>;
export const taskStartedSchema = z.object({ run: z.string().min(1).max(256) }).strict();

/**
 * What a provider's `tasks.list` route answers: the user's automations a task may run (those
 * with a `task` trigger, published and active). Its first call installs the provider's
 * `templates`; the automation installed from the template marked `default` is answered
 * `default: true`, which the core makes a new assistant's default.
 */
export const taskAutomationsSchema = z
  .object({
    automations: z
      .array(
        z
          .object({
            id: z.string().min(1).max(128),
            name: z.string().min(1).max(160),
            summary: z.string().max(500).default(''),
            default: z.boolean().optional(),
          })
          .strict(),
      )
      .max(500),
  })
  .strict();
export type TaskAutomations = z.infer<typeof taskAutomationsSchema>;

/**
 * What the core sends a provider's `tasks.adopt` route, per user (migration only; the provider
 * answers the same for the same input): the user's former registry of workflows and their
 * assistants' schedules (`assistant.wakes`), in order. Answered `taskAdoptedSchema`.
 */
export const taskAdoptInputSchema = z
  .object({
    workflows: z
      .array(
        z
          .object({
            id: z.string().min(1).max(128),
            provider: z.string().min(1).max(128),
            definition: z.string().min(1).max(512),
            name: z.string().trim().min(1).max(80),
            summary: z.string().max(300).default(''),
            isDefault: z.boolean().default(false),
          })
          .strict(),
      )
      .max(200)
      .default([]),
    schedules: z
      .array(
        z
          .object({
            assistant: z
              .object({ id: z.string().min(1).max(128), name: z.string().min(1).max(80) })
              .strict(),
            cronExpression: z.string().min(1).max(120),
            timezone: z.string().min(1).max(64),
            /** The workflow of `workflows` it started; none: the assistant's default. */
            workflowId: z.string().min(1).max(128).optional(),
          })
          .strict(),
      )
      .max(500)
      .default([]),
  })
  .strict();
export type TaskAdoptInput = z.input<typeof taskAdoptInputSchema>;

/**
 * What replaced them: by workflow id, the automation that runs each workflow (one the provider
 * does not run is left out); by index in `schedules`, the automation made for each schedule.
 */
export const taskAdoptedSchema = z
  .object({
    workflows: z.record(z.string(), z.string().min(1).max(128)),
    schedules: z
      .array(
        z
          .object({
            index: z.number().int().nonnegative(),
            automationId: z.string().min(1).max(128),
          })
          .strict(),
      )
      .max(500),
  })
  .strict();
export type TaskAdopted = z.infer<typeof taskAdoptedSchema>;

/**
 * `tasks.start` (the `tasks` permission): a plugin asks an assistant of its user for a task, as
 * the assistant's channel would (docs/adr-tasks-and-automations.md). `automation` names one of
 * the automations the assistant may run (an id or a name; none: its default, and without one an
 * agent does it alone). With `notify`,
 * the task's end reaches `POST /triggers/<notify>` as the event `crowdex/task.ended`
 * (`kind: 'event'`; its `fields.conversation` and `fields.state`, its title what it ended with).
 * Answered `taskStartAnswerSchema`.
 */
export const taskStartInputSchema = z
  .object({
    /** The assistant, by its id. */
    assistant: z.string().min(1).max(128),
    /** What it is asked to do: the task's text. */
    request: z.string().trim().min(1).max(100_000),
    automation: z.string().min(1).max(128).optional(),
    /** Who asks, as the task shows it; the plugin's title when left out. */
    requester: z.string().trim().min(1).max(200).optional(),
    notify: triggerIdSchema.optional(),
  })
  .strict();
export type TaskStartInput = z.input<typeof taskStartInputSchema>;
export const taskStartAnswerSchema = z
  .object({
    /** The task's conversation: its id in the Tasks tab and in its `crowdex/task.ended`. */
    conversation: z.uuid(),
    /** What runs it, as the assistant names it ("Standard"); null: an agent does it alone. */
    automation: z.string().nullable(),
  })
  .strict();
export type TaskStartAnswer = z.infer<typeof taskStartAnswerSchema>;
/**
 * What a task provider tells the core with `tasks.rename` (docs/adr-tasks-and-automations.md,
 * "Automations are the user's"): automations of the request's folder it imported under new ids
 * (`daily` → `daily-2`). The core renames them where it named them for that folder: the
 * `automation_ids` and `default_automation` of the assistants whose channel works there, and their
 * tasks. Answered `{ assistants, tasks }`, how many it changed.
 */
export const taskRenameInputSchema = z
  .object({
    automations: z.record(z.string().min(1).max(128), z.string().min(1).max(128)),
  })
  .strict();
export type TaskRenameInput = z.infer<typeof taskRenameInputSchema>;
/** What the core sends `tasks.stop`: the task and its run, which ends as stopped. */
export const taskStopSchema = z
  .object({ task: z.uuid(), run: z.string().min(1).max(256) })
  .strict();
export type TaskStop = z.infer<typeof taskStopSchema>;

/** What a provider reports of a task's run (`tasks.report`): its state, progress, cost, result. */
export const taskReportInputSchema = z
  .object({
    task: z.uuid(),
    state: z.enum(['running', 'waiting', 'succeeded', 'failed', 'stopped']),
    progress: z
      .object({
        /** The step running now or waiting, by its display name ("Review"). */
        step: z.string().max(200).nullable(),
        /** The review round inside a loop ("round 2 of 3"), when there is one. */
        round: z
          .object({ current: z.number().int().min(1), max: z.number().int().min(1) })
          .nullable(),
        /** A run that plans its work (Work as a team): its waves and items. */
        plan: assistantRunPlanSchema.nullable().optional(),
      })
      .strict()
      .optional(),
    costUsd: z.number().nonnegative().nullable().optional(),
    /** What it ended with (its pull request, its summary), told to the conversation that asked. */
    result: z.string().trim().min(1).max(4000).optional(),
  })
  .strict();
export type TaskReportInput = z.infer<typeof taskReportInputSchema>;

// --- requests: what an app asks the user -----------------------------------------------------

/**
 * `requests.create` (permission `requests`, docs/adr-tasks-and-roles.md §5): asks the user for an
 * approval or an answer, shown and answered as an agent's are (Needs you, a notification, the
 * conversation, the assistant). The answer reaches `POST /triggers/<id>` as
 * `{ kind: 'request', id, answer }`, durably. Creating it again while it waits tells the user
 * again (a reminder).
 */
export const requestCreateInputSchema = z
  .object({
    /** The app's id for it (`triggerIdSchema`): its answer comes to that trigger. */
    id: triggerIdSchema,
    kind: z.enum(['approval', 'question']),
    title: z.string().trim().min(1).max(200),
    /** What is asked, in a few lines: the command to approve, the question. */
    detail: z.string().trim().max(4000).optional(),
    /** A question's answers to pick from; without them it is answered in words. */
    choices: z.array(z.string().trim().min(1).max(200)).min(2).max(10).optional(),
    /** The conversation it is about (one the app started): Needs you shows it there. */
    conversation: z.uuid().optional(),
    /** Past it, an answer is refused. */
    expiresAt: z.iso.datetime({ offset: true }).optional(),
  })
  .strict()
  .refine((input) => input.kind === 'question' || input.choices === undefined, {
    message: 'Only a question has choices.',
  });
export type RequestCreateInput = z.infer<typeof requestCreateInputSchema>;

/** `requests.close`: the app no longer waits (answered in its own page, expired, stopped). */
export const requestCloseInputSchema = z.object({ id: triggerIdSchema }).strict();

/** What the user answered: an approval's decision, a question's answer, a comment with either. */
export type RequestAnswer = {
  /** An approval's decision; `false` also declines a question. */
  approved?: boolean;
  /** A question's answer: one of its choices, or the user's words. */
  answer?: string;
  /** What the user added (a review's changes). */
  comment?: string;
};

// --- connections: catalog actions and sources --------------------------------------------------

/**
 * `connections.actions.*` and `connections.sources.*` (permission `connections.actions`): the
 * actions and triggers of the catalog services the user installed, run with their connections. A
 * service is a catalog id (`slack`); an action or trigger is its id in the service (`send-message`) or
 * one of its component keys; a connection is a hub id (`connections.list`).
 */
const connectorServiceSchema = z.string().regex(/^[a-z0-9_][a-z0-9_-]{0,79}$/);
const connectorNodeSchema = z.string().regex(/^[a-zA-Z0-9_][a-zA-Z0-9_.-]{0,159}$/);
const componentVersionSchema = z.string().regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/);
const jsonObjectSchema = z.record(z.string(), z.json());

export const connectorCatalogInputSchema = z
  .object({
    query: z.string().max(200).optional(),
    service: connectorServiceSchema.optional(),
    limit: z.number().int().min(1).max(100).default(20),
    offset: z.number().int().min(0).default(0),
  })
  .strict();
export type ConnectorCatalogInput = z.input<typeof connectorCatalogInputSchema>;
const connectorCatalogNodeSchema = z.object({
  id: z.string(),
  /** The pinned component it runs by default, and its version. */
  key: z.string(),
  version: z.string(),
  name: z.string(),
  description: z.string(),
});
export const connectorCatalogSchema = z.object({
  services: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string().optional(),
      categories: z.array(z.string()),
      iconUrl: z.string().optional(),
      homepageUrl: z.string().optional(),
      /** The ways to connect it; `none` when its actions need no account. */
      auth: z.array(z.object({ id: z.string(), type: z.string(), label: z.string() })),
      actions: z.array(connectorCatalogNodeSchema),
      triggers: z.array(connectorCatalogNodeSchema),
    }),
  ),
  total: z.number().int(),
});
export type ConnectorCatalog = z.infer<typeof connectorCatalogSchema>;

export const connectorDescribeInputSchema = z
  .object({ service: connectorServiceSchema, action: connectorNodeSchema })
  .strict();
export type ConnectorDescribeInput = z.input<typeof connectorDescribeInputSchema>;
export const connectorActionDescriptionSchema = connectorCatalogNodeSchema.extend({
  service: z.string(),
  kind: z.enum(['action', 'trigger']),
  runnable: z.boolean(),
  /** The services whose connection it runs with; empty when it needs none. */
  connections: z.array(z.string()),
  inputSchema: z.record(z.string(), z.unknown()),
  props: z.array(
    z.object({
      name: z.string(),
      title: z.string(),
      type: z.string(),
      required: z.boolean(),
      secret: z.boolean(),
      description: z.string().optional(),
      defaultValue: z.unknown().optional(),
      options: z.array(z.unknown()).optional(),
      /** Its options come from `actions.options`. */
      dynamic: z.boolean(),
    }),
  ),
  /** It only reads (true), it changes things (false), or it does not say (null). */
  readOnly: z.boolean().nullable().default(null),
});
export type ConnectorActionDescription = z.infer<typeof connectorActionDescriptionSchema>;

export const connectorRunInputSchema = z
  .object({
    service: connectorServiceSchema,
    action: connectorNodeSchema,
    connection: z.uuid().optional(),
    input: jsonObjectSchema.default({}),
  })
  .strict();
export type ConnectorRunInput = z.input<typeof connectorRunInputSchema>;
/** An action's value and its exports (`$summary`, named values). */
export const connectorRunSchema = z.object({ output: z.json(), exports: jsonObjectSchema });
export type ConnectorRun = z.infer<typeof connectorRunSchema>;

export const connectorOptionsInputSchema = z
  .object({
    service: connectorServiceSchema,
    action: connectorNodeSchema,
    prop: z.string().min(1).max(160),
    /** The values entered so far, which the options may depend on. */
    input: jsonObjectSchema.default({}),
    connection: z.uuid().optional(),
  })
  .strict();
export type ConnectorOptionsInput = z.input<typeof connectorOptionsInputSchema>;
export const connectorOptionsSchema = z.object({
  options: z.array(z.object({ label: z.string(), value: z.json() })),
});
export type ConnectorOptions = z.infer<typeof connectorOptionsSchema>;

const connectorTriggerSchema = {
  service: connectorServiceSchema,
  trigger: connectorNodeSchema,
  /** The component version it was activated with: a newer catalog version is refused. */
  version: componentVersionSchema.optional(),
};
export const connectorSourceDescribeInputSchema = z.object(connectorTriggerSchema).strict();
export type ConnectorSourceDescribeInput = z.input<typeof connectorSourceDescribeInputSchema>;
export const connectorSourceDescriptionSchema = z.object({
  /** It receives events on an HTTP endpoint (`endpoint` of `sources.run`). */
  http: z.boolean(),
  /** Its polling timer (`{ intervalSeconds }`, `{ cron }`), or null. */
  timer: z.record(z.string(), z.unknown()).nullable(),
  dedupe: z.enum(['unique', 'last', 'greatest']).optional(),
});
export type ConnectorSourceDescription = z.infer<typeof connectorSourceDescriptionSchema>;

/** Stateless: the caller keeps the state a task returns and passes it to the next one. */
export const connectorSourceRunInputSchema = z
  .object({
    ...connectorTriggerSchema,
    connection: z.uuid().optional(),
    task: z.enum(['deploy', 'activate', 'deactivate', 'source']),
    props: jsonObjectSchema.default({}),
    state: z.record(z.string(), jsonObjectSchema).default({}),
    /** The event a `source` task handles (an HTTP request it received); `{}` to poll. */
    event: jsonObjectSchema.default({}),
    /** The URL its HTTP interface receives events on. */
    endpoint: z.url().max(2048).optional(),
  })
  .strict();
export type ConnectorSourceRunInput = z.input<typeof connectorSourceRunInputSchema>;
export const connectorSourceRunSchema = connectorSourceDescriptionSchema.extend({
  events: z.array(z.object({ event: z.json(), metadata: z.json() })),
  /** What a `source` task answered the HTTP request with. */
  responses: z.array(z.json()),
  state: z.record(z.string(), jsonObjectSchema),
});
export type ConnectorSourceRun = z.infer<typeof connectorSourceRunSchema>;

/** `connections.list` of a catalog service the plugin does not name (`connections.actions`). */
export const connectorConnectionsInputSchema = z.object({ type: connectorServiceSchema }).strict();
